import React, { useMemo, useRef, useEffect } from "react";
import { useMeeting } from "../../config/sdkConfig";
import { MemoizedParticipantGrid } from "../../components/ParticipantGrid";
import { useSiteContext } from "../../context/SiteContext";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import { useAgoraRTM } from "../../context/AgoraRTMContext";

// Helper to sort participants by speaking time (descending)
const sortBySpeakingTime = (ids, speakingTimes, localParticipantId) => {
  return [...ids].sort((a, b) => {
    // Local participant always stays first
    if (a === localParticipantId) return -1;
    if (b === localParticipantId) return 1;

    const timeA = speakingTimes[a] || 0;
    const timeB = speakingTimes[b] || 0;
    return timeB - timeA; // Descending order (most speaking time first)
  });
};

function ParticipantsViewer({ isPresenting, isFlexCol, layout }) {
  const {
    participants,
    pinnedParticipants,
    activeSpeakerId,
    sustainedSpeakerId,
    localParticipant,
    localScreenShareOn,
    presenterId,
  } = useMeeting();
  const { hideSelfView, showNonVideoParticipants, localPinnedParticipant, meetingRole } = useSiteContext();
  const { currentFocusParticipantId, speakingTimes } = useMeetingAppContext();
  const rtm = useAgoraRTM();
  const previousActiveSpeakerIdRef = useRef(null);
  const currentActiveSpeakerIdRef = useRef(null);
  const stableIdsRef = useRef([]);

  // Track the last active speaker ID (previous speaker before current)
  useEffect(() => {
    if (activeSpeakerId && activeSpeakerId !== currentActiveSpeakerIdRef.current) {
      // Store the previous active speaker before updating
      if (currentActiveSpeakerIdRef.current) {
        previousActiveSpeakerIdRef.current = currentActiveSpeakerIdRef.current;
      }
      currentActiveSpeakerIdRef.current = activeSpeakerId;
    } else if (!activeSpeakerId) {
      // If activeSpeakerId becomes null, clear both refs
      currentActiveSpeakerIdRef.current = null;
      previousActiveSpeakerIdRef.current = null;
    }
  }, [activeSpeakerId]);

  const isRecorderMode = new URLSearchParams(window.location.search).get('recorder') === 'true';

  const participantIds = useMemo(() => {
    // Guard: if localParticipant is not yet available (e.g., during disconnect), return empty
    // Recorder mode: no local participant — just show all remote participants
    if (!localParticipant) {
      if (isRecorderMode) return [...participants.keys()];
      return [];
    }

    // Get pinned participant IDs, but ONLY include those that actually exist in participants Map
    // This prevents duplicates when pinnedParticipants stores both string and numeric versions of same ID
    const allPinnedKeys = [...pinnedParticipants.keys()];
    const participantKeySet = new Set([...participants.keys()].map(k => String(k)));
    const pinnedParticipantId = allPinnedKeys.filter(key =>
      participantKeySet.has(String(key)) || participants.has(key)
    );
    // Deduplicate by converting to string and back
    const uniquePinnedIds = [...new Set(pinnedParticipantId.map(id => String(id)))];
    // Convert back to original type if needed (find matching key in participants)
    const normalizedPinnedIds = uniquePinnedIds.map(strId => {
      // Find the actual key in participants that matches
      for (const key of participants.keys()) {
        if (String(key) === strId) return key;
      }
      return strId;
    });

    const regularParticipantIds = [...participants.keys()].filter(
      (participantId) => {
        // Check if this participant is pinned (compare as strings to handle type differences)
        const isPinned = normalizedPinnedIds.some(pinnedId => String(pinnedId) === String(participantId));
        return !isPinned && localParticipant.id !== participantId;
      }
    );

    // Pass all participants - ParticipantGrid will handle pagination
    // IMPORTANT: Filter out localPinnedParticipant IDs that are already in regularParticipantIds
    // to prevent duplicates (compare as strings to handle type mismatches)
    const regularIdStrings = new Set(regularParticipantIds.map(id => String(id)));
    const filteredLocalPinned = localPinnedParticipant.filter(id => !regularIdStrings.has(String(id)));

    let ids = [
      localParticipant.id,
      ...filteredLocalPinned,
      ...regularParticipantIds,
    ];

    // Don't add active speaker if we're in speaker-top or speaker-side layout
    // (it will be shown in the main speaker view, not in sidebar)
    // IMPORTANT: Compare as strings to handle type mismatches (activeSpeakerId is string, UIDs might be numbers)
    if (activeSpeakerId && layout !== "speaker-top" && layout !== "speaker-side") {
      const activeSpeakerStr = String(activeSpeakerId);
      const hasActiveSpeaker = ids.some(id => String(id) === activeSpeakerStr);
      if (!hasActiveSpeaker) {
        // Don't replace - the active speaker might not be in the list if they just joined
        // Just skip adding them here, they'll be in participants naturally
      }
    }

    if (hideSelfView) {
      ids = ids.filter((id) => id !== localParticipant.id);
    }

    if (layout === "speaker-top" || layout === "speaker-side") {
      // Only filter out the current focus participant (the one displayed in the main speaker view).
      // Do NOT also filter by activeSpeakerId — the active speaker changes instantly via SDK events,
      // but currentFocusParticipantId updates a frame later. Filtering both causes a timing gap
      // where two participants get removed momentarily, then one reappears, creating visual fluctuation.
      if (currentFocusParticipantId) {
        const focusIdStr = String(currentFocusParticipantId);
        ids = ids.filter((id) => String(id) !== focusIdStr);
      }
    }

    if (showNonVideoParticipants) {
      ids = ids.filter((participantId) => {
        const participant = participants.get(participantId);
        return participant?.webcamOn == true;
      });
    }

    // Prioritize pinned participants at the top of the list (but don't replace the entire list)
    if (normalizedPinnedIds.length > 0) {
      // Filter out pinned participants from the current list (compare as strings)
      ids = ids.filter(id => !normalizedPinnedIds.some(pinnedId => String(pinnedId) === String(id)));
      // Add pinned participants at the beginning
      ids = [...normalizedPinnedIds, ...ids];
    }

    // Remove duplicate ids from ids array - use string comparison for deduplication
    const seenIds = new Set();
    ids = ids.filter(id => {
      const idStr = String(id);
      if (seenIds.has(idStr)) {
        return false;
      }
      seenIds.add(idStr);
      return true;
    });

    // Sort non-pinned participants by total speaking time (descending).
    // Keep local participant first, then pinned, then sorted by speaking time.
    // For <=20 participants on desktop the layout is FROZEN — no shuffling — so
    // hosts/attendees never see the grid reorder under them. Mobile uses the
    // same threshold for consistency. Above 20 we re-sort by speaking time so
    // the most-active speakers stay near the top of larger meetings.
    const pinnedSet = new Set(normalizedPinnedIds.map(id => String(id)));
    const localIdStr = String(localParticipant.id);

    const FREEZE_LAYOUT_THRESHOLD = 20;
    if (ids.length > FREEZE_LAYOUT_THRESHOLD) {
      const nonPinnedIds = ids.filter(id =>
        String(id) !== localIdStr && !pinnedSet.has(String(id))
      );
      const sortedNonPinned = sortBySpeakingTime(nonPinnedIds, speakingTimes, localParticipant.id);

      // Reconstruct: local first (if not hidden), then pinned, then sorted by speaking time
      const hasLocalId = ids.some(id => String(id) === localIdStr);
      const localId = hasLocalId ? [localParticipant.id] : [];
      ids = [...localId, ...normalizedPinnedIds.filter(id => String(id) !== localIdStr), ...sortedNonPinned];
    }

    // POSITION #1 (gallery, <=20 participants):
    //   - If someone is the sustained speaker (spoke for 8s continuously),
    //     they take #1 and stay there until replaced by another sustained
    //     speaker (sustainedSpeakerId is sticky — it doesn't clear on silence).
    //   - Otherwise fall back to the host/moderator. This covers the initial
    //     state where no one has crossed the 8s threshold yet.
    //
    // Net behavior: meeting starts with host at #1. When another participant
    // talks long enough to become sustained speaker, they take #1 and the
    // host drops to their natural position. Host returns to #1 only if they
    // become the next sustained speaker. This matches the "host anchor when
    // silent, active speaker wins when conversation happens" behavior.
    // Skip the position-#1 swap in small meetings where all tiles are
    // already large enough that repositioning adds visible churn without
    // visibility benefit. Thresholds:
    //   - Mobile: > 3 participants required for swap
    //   - Desktop: > 20 participants required for swap
    const isMobileView = typeof window !== 'undefined' && window.matchMedia?.('(max-width: 768px)')?.matches;
    const swapMinParticipants = isMobileView ? 3 : 20;
    const suppressSwap = ids.length <= swapMinParticipants;

    if (ids.length <= FREEZE_LAYOUT_THRESHOLD && !suppressSwap) {
      let firstId = null;

      // Priority 1: sustained speaker if present and in the visible list
      if (sustainedSpeakerId) {
        const sustainedStr = String(sustainedSpeakerId);
        const match = ids.find(id => String(id) === sustainedStr);
        if (match !== undefined) firstId = match;
      }

      // Priority 2 (fallback): host/moderator
      if (firstId === null) {
        for (const id of ids) {
          const idStr = String(id);
          if (idStr === localIdStr) {
            if (['moderator', 'host'].includes(meetingRole)) {
              firstId = id;
              break;
            }
            continue;
          }
          const p = participants.get(id);
          const pRole = p?.metaData?.meetingRole;
          if (['moderator', 'host'].includes(pRole)) {
            firstId = id;
            break;
          }
          const presenceStates = rtm?.presenceSnapshot?.get?.(idStr)?.states;
          if (presenceStates && ['moderator', 'host'].includes(presenceStates.role)) {
            firstId = id;
            break;
          }
        }
      }

      if (firstId !== null) {
        ids = ids.filter(id => String(id) !== String(firstId));
        ids.unshift(firstId);
      }
    } else if (sustainedSpeakerId && (!layout || layout === 'gallery')) {
      // Larger meetings (>20): move sustained speaker to the front so
      // attendees on the first page can follow the conversation flow.
      const sustainedStr = String(sustainedSpeakerId);
      const currentIdx = ids.findIndex(id => String(id) === sustainedStr);
      if (currentIdx > 0) {
        const [speakerId] = ids.splice(currentIdx, 1);
        ids.unshift(speakerId);
      }
    }

    // Stabilize: if the actual IDs and order haven't changed, return the
    // previous array reference so downstream components don't re-render.
    // This is critical because deps like activeSpeakerId and speakingTimes
    // change frequently but often don't alter the final ordering for <=10.
    const prev = stableIdsRef.current;
    if (
      prev.length === ids.length &&
      prev.every((id, i) => String(id) === String(ids[i]))
    ) {
      return prev;
    }
    stableIdsRef.current = ids;
    return ids;
  }, [
    participants,
    localParticipant,
    activeSpeakerId,
    sustainedSpeakerId,
    pinnedParticipants,
    presenterId,
    hideSelfView,
    localScreenShareOn,
    showNonVideoParticipants,
    layout,
    localPinnedParticipant,
    currentFocusParticipantId,
    speakingTimes,
    meetingRole,
    rtm?.presenceSnapshot,
  ]);

  return (
    <MemoizedParticipantGrid
      participantIds={participantIds}
      isPresenting={isPresenting}
      isFlexCol={isFlexCol}
      layout={layout}
    />
  );
}

const MemorizedParticipantView = React.memo(
  ParticipantsViewer,
  (prevProps, nextProps) => {
    return prevProps.isPresenting === nextProps.isPresenting;
  }
);

export default MemorizedParticipantView;
